// ─────────────────────────────────────────────────────────────────────────────
// QA/QC → Control Charts — shared types and PRESENTATION helpers.
//
// The seeded sample-data generator that used to live here is gone: the screen
// now runs on the LIMS's own QC results (dbo.SampleParameter), fetched through
// controlChartApi.ts.  Everything that decides what a chart SAYS — which column
// is plotted, the mean, the SD, the ±2SD / ±3SD limits, the out-of-control
// count, the saved-chart status and the signature fingerprint — is computed by
// the API (CLAUDE.md §19.1: all business logic lives in the API).
//
// What remains here is the vocabulary the UI renders with: the row/stat shapes,
// number formatting, the acceptance-rule evaluation used by the report's
// findings table, the chart-canvas themes, and the signature wording.
//
// Reference implementation for the ported calculations and the retrieve
// predicate: ALPACALIMS_GoLive
// BTLIMS.Module/Controllers/QAQC/ControlChartController.cs.
// ─────────────────────────────────────────────────────────────────────────────

/** How a parameter's limits are drawn.
 *
 *  `standard` is the only kind the LIMS can source today — an individuals
 *  (Levey-Jennings) chart with mean ± 2SD warning and ± 3SD control limits.
 *  The other three belong to microbiological charts (duplicate RANGE, positive
 *  control on log₁₀ count, negative-control STERILITY); their canvases and
 *  statistics strips are retained because they are part of the approved design,
 *  but no column in the current schema records duplicate counts or colony
 *  counts, so the API never returns them. */
export type ChartKind = 'standard' | 'range' | 'individuals' | 'attribute' | 'presence';

/** Saved-chart outcome. `Unknown` is what a chart created by the legacy XAF
 *  screen reads as — it has no companion row, so nobody computed a status for
 *  it and inventing one would be worse than saying so. */
export type ChartStatus = 'In control' | 'Warning' | 'Out of control' | 'Unknown';

/** Which numeric column the chart plots — the whole screen keys off this.
 *  Chosen server-side from the QC root roles: a duplicate charts its RPD, a
 *  blank its numeric result, everything else its % recovery (the legacy
 *  `QCRootRole` switch, ControlChartController.Calculation). */
export type Field = 'rec' | 'rpd' | 'res' | 'rng' | 'logc' | 'growth' | 'pa';

/** One selectable test — a Matrix / Test / Method triple with the number of
 *  chartable QC results behind it. */
export interface TestDef {
  testMethodId: string;
  matrix: string;
  test: string;
  method: string;
  records: number;
  /** Set only by a microbiological data source; never by the current API. */
  kind?: 'micro';
  unit?: string;
}

export interface QcTypeDef { name: string; role: string; rootRole?: string }

/** One QC result on the chart, as the API returns it. */
export interface QcRow {
  /** Analysis date, `yyyy-MM-dd`. A string on the wire so it cannot be
   *  timezone-shifted — this is a legacy/Central column (ADR-0008). */
  d: string;
  test: string | null;
  method: string | null;
  matrix: string | null;
  /** Parameter. */
  p: string | null;
  /** QC type. */
  qt: string | null;
  /** QC root role — Spike / Duplicate / Blank / Standard. */
  qr: string | null;
  inst: string | null;
  anly: string | null;
  batch: string | null;
  sample: string | null;
  rec: number | null;
  rpd: number | null;
  /** Numeric result — what a BLANK chart plots. */
  res: number | null;
  oid?: string;
  // Microbiological columns — no source in the current schema (see ChartKind).
  unit?: string;
  c1?: number | null;
  c2?: number | null;
  rng?: number | null;
  logc?: number;
  target?: number;
  growth?: number;
  /** Presence/Absence: 1 = present, 0 = absent. Charted for a P/A positive control. */
  pa?: number | null;
}

/** Per-parameter control limits, computed by the API. */
export interface Stat {
  parameter: string;
  kind: ChartKind;
  mean: number | null;
  std: number | null;
  ucl: number | null;
  uwl: number | null;
  lwl: number | null;
  lcl: number | null;
  count: number;
  /** How many of this parameter's points fall outside the control limits. One
   *  server-side definition, so the status pill, the statistics grid and the
   *  saved-chart status can never disagree. Optional only so a stat can be
   *  hand-built in a test; the API always sends it. */
  outOfControl?: number;
  // Microbiological extras — see ChartKind.
  rbar?: number;
  maxR?: number;
  meanCount?: number;
  fail?: number;
  pass?: number;
  rate?: number;
  maxGrowth?: number;
}

export interface Signature {
  by: string;
  role: string | null;
  uid: string | null;
  at: string;
  meaning: string;
  comment: string | null;
  hash: string;
}

export interface SavedChart {
  oid: string;
  ccid: string;
  matrix: string | null;
  test: string | null;
  method: string | null;
  qc: string;
  from: string | null;
  to: string | null;
  params: number;
  by: string | null;
  on: string | null;
  status: ChartStatus;
  sig?: Signature | null;
}

// ── Number formatting ────────────────────────────────────────────────────────

/** Three significant figures, or an em-dash when the statistic does not exist
 *  for this chart kind. A blank cell would read as "not computed". */
export const sig = (n: number | null | undefined): string =>
  (n === null || n === undefined || !Number.isFinite(n)) ? '—' : String(Number(n.toPrecision(3)));

// ── What the axis is measuring ───────────────────────────────────────────────

export const valueLabel = (f: Field) =>
  f === 'rng' ? 'Range of log₁₀ counts (R)'
    : f === 'logc' ? 'log₁₀ count (positive control)'
      : f === 'growth' ? 'Colonies in blank'
        : f === 'pa' ? 'Presence of the positive control'
          : f === 'rpd' ? 'RPD'
            : f === 'res' ? 'Result'
              : '% Recovery';

/** Short caption form, for the card headers. */
export const valueShort = (f: Field) =>
  f === 'rng' ? 'duplicate range (log₁₀)'
    : f === 'logc' ? 'positive-control log₁₀ count'
      : f === 'growth' ? 'sterility of the negative control'
        : f === 'pa' ? 'presence of the positive control'
          : f === 'rpd' ? 'RPD'
            : f === 'res' ? 'the blank result'
              : '% recovery';

export const isMicro = (t: TestDef | null) => !!(t && t.kind === 'micro');

/** Reads the charted column off a row — the client mirror of the API's own
 *  `ValueOf`, used only to plot points that the API has already selected. */
export const valueOf = (r: QcRow, f: Field): number | null | undefined =>
  (r as unknown as Record<string, number | null | undefined>)[f];

// ── Acceptance rules ─────────────────────────────────────────────────────────
// Evaluated on the client because they read the ALREADY-computed limits and
// produce narrative for the report's findings table — they add no new numbers.

export interface RulePoint { i: number; d: string; v: number; batch: string | null; anly: string | null }
export interface Violation { rule: string; i: number; p: RulePoint }

export function evaluateRules(vals: RulePoint[], st: Stat): Violation[] {
  /* A RANGE chart is not judged by the Westgard rules. Those rules read the
     distance of a point from a mean in units of SD, and assume the series is
     free to wander either side of it. A range cannot go below zero, its
     distribution is skewed, and Standard Methods 9020 B gives it exactly one
     criterion: R must not exceed 3.27 R̄. Applying 1-3s or 10x here would
     manufacture violations out of the shape of the distribution. */
  if (st.kind === 'range') {
    return vals.filter(p => st.ucl !== null && p.v > st.ucl)
      .map(p => ({ rule: '>3.27R̄', i: p.i - 1, p }))
      .slice(0, 6);
  }
  /* Sterility is pass/fail. Every blank that grew is a signal in its own right —
     there is no run to detect and no limit to drift. */
  if (st.kind === 'attribute') {
    return vals.filter(p => p.v > 0).map(p => ({ rule: 'Growth', i: p.i - 1, p })).slice(0, 8);
  }
  /* Presence/Absence positive control is pass/fail with the OPPOSITE criterion:
     it must be present, so every ABSENT (v < 1) is a signal. */
  if (st.kind === 'presence') {
    return vals.filter(p => p.v < 1).map(p => ({ rule: 'Absent', i: p.i - 1, p })).slice(0, 8);
  }
  if (!st.std || vals.length < 2) return [];
  const z = vals.map(p => (p.v - st.mean!) / st.std!);
  const out: Violation[] = [];
  const add = (rule: string, i: number) => out.push({ rule, i, p: vals[i]! });

  z.forEach((v, i) => { if (Math.abs(v) > 3) add('1-3s', i); });
  for (let i = 1; i < z.length; i++) {
    if (Math.abs(z[i]!) > 2 && Math.abs(z[i - 1]!) > 2 && Math.sign(z[i]!) === Math.sign(z[i - 1]!)) add('2-2s', i);
    if (Math.abs(z[i]! - z[i - 1]!) > 4) add('R-4s', i);
  }
  for (let i = 3; i < z.length; i++) {
    const w = z.slice(i - 3, i + 1);
    if (w.every(x => x > 1) || w.every(x => x < -1)) add('4-1s', i);
  }
  for (let i = 9; i < z.length; i++) {
    const w = z.slice(i - 9, i + 1);
    if (w.every(x => x > 0) || w.every(x => x < 0)) add('10x', i);
  }
  for (let i = 6; i < z.length; i++) {
    const w = z.slice(i - 6, i + 1);
    let up = true;
    let dn = true;
    for (let k = 1; k < w.length; k++) {
      if (w[k]! <= w[k - 1]!) up = false;
      if (w[k]! >= w[k - 1]!) dn = false;
    }
    if (up || dn) add('7T', i);
  }
  // One entry per DISTINCT rule, earliest occurrence first — a report listing
  // "1-3s" forty times says no more than listing it once.
  const seen = new Set<string>();
  return out.sort((a, b) => a.i - b.i).filter(v => !seen.has(v.rule) && seen.add(v.rule));
}

/** Disposition follows the rule that fired: a rejection rule stops the batch, a
 *  run rule opens an investigation. Stating it is what makes the report an
 *  auditable record rather than an observation. */
export function dispositionOf(rule: string): string {
  if (rule === 'Growth') return 'Batch invalidated; media lot and dilution water re-sterilised and re-tested';
  if (rule === 'Absent') return 'Positive control failed to grow — media performance and incubation reviewed; batch re-tested';
  if (rule === '>3.27R̄') return 'Duplicate repeated; analyst technique and dilution reviewed';
  if (rule === '1-3s' || rule === 'R-4s') return 'Batch rejected and re-analysed; limits held';
  if (rule === '10x' || rule === '7T') return 'Systematic shift — limits recalculated and reset';
  return 'Investigated; cause documented, limits held';
}

/** What each rule means, for the Guide and the report's rules table. */
export const RULE_TEXT: [string, string][] = [
  ['1-3s', 'One point beyond ±3SD — rejection. The batch is out of control.'],
  ['2-2s', 'Two consecutive points beyond the same ±2SD limit — systematic error.'],
  ['R-4s', 'Two consecutive points differing by more than 4SD — random error.'],
  ['4-1s', 'Four consecutive points beyond the same ±1SD — developing bias.'],
  ['10x', 'Ten consecutive points on one side of the mean — a shift.'],
  ['7T', 'Seven consecutive points trending in one direction — drift.'],
  ['>3.27R̄', 'Duplicate range beyond the SM 9020 B criterion — precision failure.'],
  ['Growth', 'Any growth in a negative control — sterility failure.'],
  ['Absent', 'Positive control absent — it must be present; a media/incubation failure.'],
];

// ── Electronic signature ─────────────────────────────────────────────────────
// (Report Signing precedent, ADR-0009.)
//
// A saved control chart IS a QC record: it fixes the limits an analyst will
// judge tomorrow's results against. So it is signed, not merely saved — the same
// three-part signature the reporting module uses: something the signer KNOWS
// (password), a SECOND factor, and an explicit ATTESTATION carrying the meaning
// of the signature. 21 CFR Part 11 §11.50 wants the signer's printed name, the
// date and time, and the MEANING recorded with the record itself, which is why
// the meaning is a chosen value and not a free-text note.
//
// The password and the 6-digit code are verified SERVER-SIDE (the login seam +
// the signer's signing authenticator) and are never persisted. The chart
// definition is fingerprinted at signing; re-query it over a different window
// and the signature reads as stale rather than appearing to cover data it never
// saw.

export const SIG_MEANINGS = [
  'Reviewed — an accurate record of the QC data for this period',
  'Approved — these limits are released for judging routine QC',
  'Rejected — out of control; corrective action raised',
];

export const SIG_ERRS: Record<string, string> = {
  pw: 'Enter your account password.',
  otp: 'Enter the 6-digit code from your authenticator.',
  att: 'Tick the attestation to apply your signature.',
  bad: 'Password or second factor not accepted. The signature was not applied.',
};

export interface HashInput {
  test?: string | null;
  method?: string | null;
  qc: string;
  from: string;
  to: string;
  params: number;
  points: number;
}

/** Deterministic fingerprint (FNV-1a) of the data set on screen, for comparison
 *  with the one the API stored at signing. Identical inputs, identical order and
 *  identical algorithm to `ControlChartStatistics.Fingerprint`, so re-querying a
 *  signed chart over a different window makes its signature read as STALE rather
 *  than silently appearing to cover data it never saw. */
export function sigHash(o: HashInput): string {
  const src = [o.test, o.method, o.qc, o.from, o.to, o.params, o.points].join('|');
  let h = 0x811c9dc5;
  for (let i = 0; i < src.length; i++) {
    h ^= src.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).toUpperCase().padStart(8, '0');
}

// ── Timestamps ───────────────────────────────────────────────────────────────

/** Local wall-clock, 24-hour, per the shared date rules — never AM/PM.
 *  Built from parts so a 12-hour token is impossible (§18.6). */
export function stamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getMonth() + 1)}/${p(d.getDate())}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** The ISO date the grid sorts on — LOCAL, so it can never disagree with the
 *  signature stamp beside it (toISOString would roll the date over at 19:00
 *  Central). */
export function localISO(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// ── Audit trail ──────────────────────────────────────────────────────────────
// Standing rule 11: every form is wired to the audit trail. These rows are
// written by IAuditTrailService into dbo.AuditData — never by a form — and read
// back through the shared /api/v1/audit/trail endpoint. The full VIEW lives in
// Data Center → Audit Trail Log; this screen shows the same rows scoped to one
// chart.

export interface AuditEntry {
  at: string; op: string; who: string; form: string; src: string;
  prop: string; old: string; neu: string; why: string;
}

/** Pill tone per operation, so Created / Deleted read differently at a glance.
 *  Keyed by both the wire form (`ValueChanged`, `Deleted`) and the semantic verb
 *  the API sends (`Create`, `Update`, `Delete`). */
export const OP_TONE: Record<string, string> = {
  Created: 'ok', Create: 'ok',
  ValueChanged: 'warn', Update: 'warn',
  Deleted: 'bad', Delete: 'bad',
  Preview: '', Send: '',
};

// ── Chart themes ─────────────────────────────────────────────────────────────
// The plot is a panel with its own skin, the way a trading terminal treats a
// chart: the surrounding form stays in the app's light chrome, only the canvas
// changes. Each theme is a closed token set — background, grid, axis, the three
// limit colours and a qualitative series ramp — so adding one never means
// touching a drawing routine. PRINT is deliberately monochrome and separates its
// series by DASH rather than hue, because a colour-coded chart photocopies into
// six identical grey lines. CONTRAST is the accessibility option: pure black,
// saturated hues, white axis, for a bench monitor or a projector.
//
// These are chart-canvas tokens, NOT app chrome — the form around them still
// uses the shared theme tokens (§18.6).

export interface ChartTheme {
  name: string; swatch: string; dark: boolean; mono?: boolean;
  bg: string; grid: string; axis: string; ink: string;
  red: string; grn: string; amb: string; ser: string;
  pal: string[]; dash?: string[];
}

export const THEMES: Record<string, ChartTheme> = {
  light: { name: 'Light', swatch: '#ffffff', dark: false,
    bg: '#ffffff', grid: '#eceef1', axis: '#8a9099', ink: '#5b6068',
    red: '#d9534f', grn: '#3c9d63', amb: '#c8811f', ser: '#4a8fc7',
    pal: ['#4a8fc7', '#3c9d63', '#c8811f', '#8e6fc4', '#d9534f', '#4aa8a0', '#b8734a', '#7a8b99'] },
  dark: { name: 'Dark', swatch: '#1b1f27', dark: true,
    bg: '#1b1f27', grid: '#2c313b', axis: '#9aa2ae', ink: '#cfd5de',
    red: '#ff6b66', grn: '#55c98a', amb: '#e0a33d', ser: '#64b5f6',
    pal: ['#64b5f6', '#55c98a', '#e0a33d', '#b39ddb', '#ff6b66', '#4dd0c4', '#d4915f', '#a3adba'] },
  terminal: { name: 'Terminal', swatch: '#0a1424', dark: true,
    bg: '#0a1424', grid: '#14263f', axis: '#7e97b5', ink: '#cfe0f2',
    red: '#ff4d4d', grn: '#16d167', amb: '#ffb020', ser: '#2fd6ff',
    pal: ['#2fd6ff', '#16d167', '#ffb020', '#c07cff', '#ff4d4d', '#00e5cf', '#ff8a4c', '#8fa8c2'] },
  contrast: { name: 'Contrast', swatch: '#000000', dark: true,
    bg: '#000000', grid: '#3a3a3a', axis: '#ffffff', ink: '#ffffff',
    red: '#ff3b30', grn: '#00e05a', amb: '#ffcc00', ser: '#00c8ff',
    pal: ['#00c8ff', '#00e05a', '#ffcc00', '#d68cff', '#ff3b30', '#00e5d0', '#ff9f0a', '#ffffff'] },
  print: { name: 'Print', swatch: '#f2f2f2', dark: false, mono: true,
    bg: '#ffffff', grid: '#e4e4e4', axis: '#555555', ink: '#222222',
    red: '#111111', grn: '#777777', amb: '#555555', ser: '#111111',
    pal: ['#111111', '#555555', '#888888', '#333333', '#6b6b6b', '#9a9a9a', '#444444', '#777777'],
    dash: ['', '6 3', '2 3', '9 3 2 3', '4 4', '1 3', '11 4', '5 2 1 2'] },
};

/** How many points of a series sit outside its control limits.
 *
 *  The API computes this and puts it on the stat (`outOfControl`) so the pill,
 *  the grid and the saved-chart status share ONE definition. This reads that
 *  value; the fallback recomputes it only for a stat that predates the field. */
export function outOfControlCount(rows: QcRow[], st: Stat, field: Field): number {
  if (typeof st.outOfControl === 'number') return st.outOfControl;
  const vals = rows.filter(r => r.p === st.parameter)
    .map(r => valueOf(r, field))
    .filter((v): v is number => v !== null && v !== undefined && Number.isFinite(v));
  if (st.kind === 'attribute') return vals.filter(v => v > 0).length;
  if (st.kind === 'presence') return vals.filter(v => v < 1).length;  // absent = failure
  return vals.filter(v => (st.ucl !== null && v > st.ucl) || (st.lcl !== null && v < st.lcl)).length;
}

/** Page sizes offered by the query-panel sub-grids. */
export const PAGE_SIZES = [10, 20, 50, 100];
